import { zodResolver } from '@hookform/resolvers/zod'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import { type EdgeLabelSchemaType, edgeLabelSchema } from './agency-node-form.schema'
import { EdgeLabelFormButtons } from './edge-label-form-buttons.atom'
import { createEdgeLabelKeyHandler } from './edge-label-form-key-handler.util'

interface EdgeLabelFormProps {
  label: string
  isOpen: boolean
  onSave: (label: string) => void
  onCancel: () => void
}

export const EdgeLabelForm = ({
  label,
  isOpen,
  onSave,
  onCancel,
}: EdgeLabelFormProps): ReactNode => {
  const form = useForm<EdgeLabelSchemaType>({
    resolver: zodResolver(edgeLabelSchema),
    defaultValues: { label: '' },
  })

  useEffect(() => {
    if (isOpen) form.reset({ label })
  }, [isOpen, label, form])

  const handleSubmit = useCallback(
    (values: EdgeLabelSchemaType): void => onSave(values.label),
    [onSave]
  )
  const handleKeyDown = useMemo(
    () => createEdgeLabelKeyHandler({ form, onSubmit: handleSubmit, onCancel }),
    [form, handleSubmit, onCancel]
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-2">
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Enter edge label (e.g., delegate_task)"
                  className="edge-label-input text-sm"
                  autoFocus
                  onKeyDown={handleKeyDown}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <EdgeLabelFormButtons onCancel={onCancel} />
      </form>
    </Form>
  )
}

EdgeLabelForm.displayName = 'EdgeLabelForm'
